// Component: StockVideoForm
// Purpose: Admin form to create/edit a stock video entry. Handles upload URL
// retrieval, video selection/upload via VideoUploader, optional thumbnail via
// ThumbnailUploader, and submits FormData to parent.
import React, { useEffect, useState } from 'react';
import { getStockVideoUploadUrl } from '../../../api/media/stockVideosApi';
import { STOCK_VIDEO_CATEGORY_LIST } from '../../../data/tags/tagEnums';
import { ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import VideoUploader from '../createTemplates/VideoUploader';
import ThumbnailUploader from './ThumbnailUploader';

const StockVideoForm = ({ onSubmit, isSubmitting, initialData, submitButtonText = 'Create Stock Video' }) => {
  const [formData, setFormData] = useState({
    title: '',
    tags: [],
    searchableText: '',
    videoFile: null,
    videoUploadUrl: '',
    videoUploaded: false,
    permanentVideoUrl: '',
    thumbnailFile: null,
    ...initialData
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoadingUploadUrl, setIsLoadingUploadUrl] = useState(false);
  const [uploadUrlError, setUploadUrlError] = useState('');
  
  // For URL input values (no longer needed but keeping structure for future)
  const [urlInputValues, setUrlInputValues] = useState({});

  // Fetch video upload URL on component mount
  useEffect(() => {
    if (!formData.videoUploadUrl) {
      fetchVideoUploadUrl();
    }
  }, []);

  // Function to fetch video upload URL from the API
  const fetchVideoUploadUrl = async () => {
    setIsLoadingUploadUrl(true);
    setUploadUrlError('');
    
    try {
      const response = await getStockVideoUploadUrl();
      
      if (response && response.uploadUrl && response.permanentUploadPath) {
        setFormData(prev => ({
          ...prev,
          videoUploadUrl: response.uploadUrl,
          permanentUploadPath: response.permanentUploadPath
        }));
        
        console.log('Stock video upload URL obtained successfully');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error fetching stock video upload URL:', error);
      setUploadUrlError('Failed to get video upload URL. Please try again.');
    } finally {
      setIsLoadingUploadUrl(false);
    }
  };

  // Effect to apply initialData if provided
  useEffect(() => {
    if (initialData) {
      console.log('StockVideoForm initialData received:', initialData);
      
      setFormData(prev => ({
        ...prev,
        title: initialData.title || '',
        tags: initialData.tags || [],
        searchableText: initialData.searchableText || '',
        permanentVideoUrl: initialData.videoUrl || '',
        thumbnailFile: initialData.thumbnailFile || null
      }));

      // Clear URL input values since we removed Canva link
      setUrlInputValues({});
    }
  }, [initialData]);

  // Handle changes to form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // For regular form fields
    if (name !== 'canvaLink') {
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    
    // Note: Canva link handling has been removed - keeping this structure for future URL fields
    setUrlInputValues(prev => ({ ...prev, [name]: value }));
    
    const fullUrl = value ? `https://${value}` : '';
    setFormData(prev => ({ ...prev, [name]: fullUrl }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => {
      const isSelected = prev.tags.includes(tag);
      const newTags = isSelected
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag];
      
      return { ...prev, tags: newTags };
    });
    
    setTouched(prev => ({ ...prev, tags: true }));
    
    if (errors.tags) {
      setErrors(prev => ({ ...prev, tags: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.tags.length === 0) {
      newErrors.tags = 'At least one tag is required';
    }

    // Check for video: either existing video (edit mode) or new video uploaded/selected
    const hasExistingVideo = initialData?.videoUrl;
    const hasNewVideo = formData.videoUploaded || formData.videoFile;
    
    if (!hasExistingVideo && !hasNewVideo) {
      newErrors.videoFile = 'Video upload is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormComplete = () => {
    // Check for valid video: either existing or new
    const hasExistingVideo = initialData?.videoUrl;
    const hasNewVideo = formData.videoUploaded || formData.videoFile;
    const hasValidVideo = hasExistingVideo || hasNewVideo;
    
    return formData.title.trim() !== '' &&
           formData.tags.length > 0 &&
           hasValidVideo;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.log('Form validation failed:', errors);
      return;
    }

    // Create FormData object for submission
    const formDataObj = new FormData();
    
    // Add basic fields
    formDataObj.append('title', formData.title);
    formDataObj.append('tags', formData.tags.join(','));
    
    // Handle keywords and searchable text
    let searchableText = '';
    if (formData.searchableText) {
      searchableText = formData.searchableText;
    } else {
      searchableText = [formData.title, ...formData.tags].join(' ');
    }
    formDataObj.append('searchableText', searchableText);

    // Add video URL - handle both new uploads and existing videos
    let videoUrlToSubmit = null;
    
    if (formData.permanentVideoUrl) {
      // New video was uploaded and processed
      console.log('[StockVideoForm] Using permanentVideoUrl for submission:', formData.permanentVideoUrl);
      videoUrlToSubmit = formData.permanentVideoUrl;
    } else if (formData.permanentUploadPath) {
      // Fallback for new video uploads
      console.log('[StockVideoForm] Using permanentUploadPath as fallback:', formData.permanentUploadPath);
      videoUrlToSubmit = formData.permanentUploadPath;
    } else if (initialData?.videoUrl && !formData.videoFile) {
      // Edit mode: no new video selected, use existing video URL
      console.log('[StockVideoForm] Using existing video URL for edit mode:', initialData.videoUrl);
      videoUrlToSubmit = initialData.videoUrl;
    } else if (formData.videoFile) {
      // New video file selected but not yet uploaded (shouldn't happen in normal flow)
      console.log('[StockVideoForm] No permanent URL found but video file exists. Using placeholder.');
      videoUrlToSubmit = formData.permanentUploadPath || `gs://placeholder-for-stock-video-${Date.now()}`;
    }
    
    if (!videoUrlToSubmit) {
      console.error('[StockVideoForm] No video URL available for submission');
      setErrors(prev => ({ ...prev, videoFile: 'A video is required. Please upload a video or ensure the existing video is available.' }));
      return;
    }
    
    // This is the key fix - append as 'videoUrl' not something else
    formDataObj.append('videoUrl', videoUrlToSubmit);

    // Add thumbnail image if provided
    if (formData.thumbnailFile) {
      formDataObj.append('thumbnailImage', formData.thumbnailFile);
    }

    console.log('[StockVideoForm] Submitting stock video with data:', {
      title: formData.title,
      tags: formData.tags.join(','),
      videoUrl: videoUrlToSubmit,
      videoUploaded: formData.videoUploaded,
      hasVideoFile: !!formData.videoFile,
      hasThumbnailFile: !!formData.thumbnailFile
    });

    // Submit the form
    onSubmit(formDataObj);
  };

  const handleVideoSelected = (file) => {
    console.log('handleVideoSelected called with:', file);
    setFormData(prev => ({
      ...prev,
      videoFile: file
    }));
    
    // Clear video-related errors if a valid file is provided
    if (file && errors.videoFile) {
      setErrors(prev => ({
        ...prev,
        videoFile: ''
      }));
    }
  };
  
  // Handle successful video upload
  const handleVideoUploaded = (videoData) => {
    console.log('[StockVideoForm] handleVideoUploaded called with data:', videoData);
    
    // Once a video is successfully uploaded, update form data
    setFormData(prev => {
      const permanentVideoUrl = videoData.videoUrl || '';
      
      const updatedData = {
        ...prev,
        videoUploaded: true,
        videoMetadata: videoData,
        permanentVideoUrl: permanentVideoUrl
      };
      
      console.log('[StockVideoForm] Updated form data after video upload:', updatedData);
      return updatedData;
    });
    
    // Clear any video-related errors
    if (errors.videoFile || errors.videoUploadUrl) {
      setErrors(prev => ({
        ...prev,
        videoFile: '',
        videoUploadUrl: ''
      }));
    }
  };

  const handleThumbnailChange = (file) => {
    if (file === null) {
      // Remove thumbnail
      setFormData(prev => ({ ...prev, thumbnailFile: null }));
      setErrors(prev => ({ ...prev, thumbnailFile: null }));
    } else if (file && file.isExistingThumbnail) {
      // Handle existing thumbnail (for edit mode)
      // In this case, we don't need to do anything as the form will keep the existing thumbnail
      console.log('Using existing thumbnail:', file.name);
    } else {
      // New file selected (either uploaded or auto-generated)
      setFormData(prev => ({ ...prev, thumbnailFile: file }));
      setErrors(prev => ({ ...prev, thumbnailFile: null }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-7">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Video Title
        </label>
        <div className="mt-1">
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`block w-full rounded-md px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm
              ${errors.title && touched.title ? 'border-red-300 dark:border-red-700 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100'}`}
            placeholder="Enter a descriptive title for the stock video..."
          />
        </div>
        {errors.title && touched.title && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1" id="title-error">
            {errors.title}
          </p>
        )}
      </div>

      {/* Video Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Upload Video (Up to 500MB for 4K Quality)
        </label>
        <VideoUploader 
          onVideoSelected={handleVideoSelected} 
          onVideoUploaded={handleVideoUploaded} 
          uploadUrl={formData.videoUploadUrl} 
          error={errors.videoFile} 
          compressionTargetSizeMB={500}
          existingVideoUrl={initialData?.videoUrl}
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 ml-1">
          {initialData?.videoUrl 
            ? "Current video is shown above. Upload a new video to replace it, or leave as-is to keep the existing video."
            : "Upload high-quality videos up to 500MB. No compression will be applied to preserve 4K quality."
          }
        </p>
        {errors.videoFile && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1">
            {errors.videoFile}
          </p>
        )}
      </div>

      {/* Thumbnail Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Thumbnail Image (Optional)
        </label>
        <ThumbnailUploader 
          onThumbnailChange={handleThumbnailChange}
          error={errors.thumbnailFile}
          existingThumbnail={initialData?.thumbnailUrl ? { url: initialData.thumbnailUrl, name: 'Existing Thumbnail' } : null}
          videoFile={formData.videoFile}
          videoUrl={formData.videoUploaded ? null : (initialData?.videoUrl || formData.videoUploadUrl)}
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 ml-1">
          {formData.videoFile || (initialData?.videoUrl || formData.videoUploadUrl) ? 
            "💡 You can auto-generate a thumbnail from your video or upload a custom image. If no thumbnail is provided, the system can generate one from the first frame of your video." :
            "Upload a custom thumbnail image. Once you upload a video, you'll also have the option to auto-generate a thumbnail from the video's first frame."
          }
        </p>
      </div>

      {/* Video Upload URL Error Handling */}
      {(uploadUrlError || (!formData.videoUploadUrl && !isLoadingUploadUrl)) && (
        <div>
          <label htmlFor="videoUploadUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Video Upload URL
          </label>
          
          {uploadUrlError ? (
            <div className="mt-2 text-sm text-red-600 dark:text-red-500 flex items-center">
              <span>{uploadUrlError}</span>
              <button
                type="button"
                onClick={fetchVideoUploadUrl}
                disabled={isLoadingUploadUrl}
                className="ml-2 inline-flex items-center px-3 py-1 border border-red-300 dark:border-red-600 text-sm leading-4 font-medium rounded-md text-red-700 dark:text-red-200 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center">
              <span>No upload URL available.</span>
              <button
                type="button"
                onClick={fetchVideoUploadUrl}
                disabled={isLoadingUploadUrl}
                className="ml-2 inline-flex items-center px-3 py-1 border border-amber-300 dark:border-amber-600 text-sm leading-4 font-medium rounded-md text-amber-700 dark:text-amber-200 bg-white dark:bg-gray-800 hover:bg-amber-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Get URL
              </button>
            </div>
          )}
          
          {isLoadingUploadUrl && (
            <p className="mt-2 text-sm text-blue-600 dark:text-blue-400 flex items-center ml-1">
              <ButtonSpinner color="blue" />
              Getting upload URL...
            </p>
          )}
        </div>
      )}

      {/* Tags */}
      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tags
        </span>
        <div className="mt-2">
          <div className="flex flex-wrap gap-2">
            {STOCK_VIDEO_CATEGORY_LIST.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium transition-colors
                  ${formData.tags.includes(tag) 
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
        {errors.tags && touched.tags && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-500 ml-1">
            {errors.tags}
          </p>
        )}
      </div>

      {/* Search Terms */}
      <div>
        <label htmlFor="searchableText" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Search Terms (Optional)
        </label>
        <div className="mt-1">
          <textarea
            id="searchableText"
            name="searchableText"
            rows={3}
            value={formData.searchableText}
            onChange={handleChange}
            className="block w-full rounded-md px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
            placeholder="Add comma-separated search terms to help users find this video..."
          />
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 ml-1">
          Add search terms that describe the video content, people, places, or topics. This will make your video easily discoverable. Tags are automatically included.
        </p>
      </div>

      {/* Submit Button */}
      <div className="pt-6 flex justify-end">
        <button
          type="submit"
          disabled={!isFormComplete() || isSubmitting}
          className={`flex items-center justify-center py-2.5 px-5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
            isFormComplete() && !isSubmitting
              ? 'bg-primary-600 hover:bg-primary-700'
              : 'bg-gray-400 cursor-not-allowed opacity-75'
          }`}
        >
          {isSubmitting ? (
            <>
              <ButtonSpinner color="white" />
              Creating...
            </>
          ) : (
            submitButtonText
          )}
        </button>
      </div>
    </form>
  );
};

export default StockVideoForm; 